import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../components/ui/utils';
import type { Account } from '../../../entities/account';
import { PlatformBadge, type Platform } from '../ui/PlatformBadge';

export type AccountOption = {
  value: string;
  label: string;
  platform: Platform;
  avatar?: string;
};

/**
 * Единый список аккаунтов → опции селектора. Принимает уже смёрженный
 * Account[] (Instagram из accountStore + TikTok из useTikTokAccounts):
 * платформа берётся из acc.platform, у легаси-записей её нет = instagram.
 */
export function buildAccountOptions(accounts: Account[]): AccountOption[] {
  return accounts.map((acc) => ({
    value: acc.id,
    // У TikTok-аккаунта handle может отсутствовать — тогда показываем displayName.
    label: acc.username ? `@${acc.username}` : acc.platform === 'tiktok' ? acc.name : `@${acc.name}`,
    platform: acc.platform ?? 'instagram',
    avatar: acc.profilePicture,
  }));
}

type Props = {
  options: AccountOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

function OptionRow({ option }: { option: AccountOption }) {
  const initials = (option.label || '?').replace(/^@/, '').slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar className="size-6 shrink-0">
        <AvatarImage src={option.avatar} alt={option.label} />
        <AvatarFallback className="text-[10px] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <span className="font-medium truncate">{option.label}</span>
      <PlatformBadge platform={option.platform} />
    </div>
  );
}

/** Searchable account picker across Instagram + TikTok, tagged by platform. */
export function UnifiedAccountSelect({ options, value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const isEmpty = options.length === 0;
  const selected = options.find((o) => o.value === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isEmpty}
          className="w-full h-10 justify-between font-normal"
        >
          {selected ? (
            <OptionRow option={selected} />
          ) : (
            <span className="text-muted-foreground">
              {isEmpty ? 'Нет подключённых аккаунтов' : 'Выберите аккаунт'}
            </span>
          )}
          <ChevronsUpDown className="size-4 text-muted-foreground shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command
          filter={(itemValue, search) =>
            itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder="Поиск аккаунта…" />
          <CommandList>
            <CommandEmpty>Ничего не найдено</CommandEmpty>
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={`${option.label} ${option.platform}`}
                onSelect={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <div className="flex items-center justify-between gap-2 w-full min-w-0">
                  <OptionRow option={option} />
                  <Check
                    className={cn('size-4 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')}
                  />
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
